import { useContext, useEffect } from "react";
import {
  Modal,
  ModalBody,
  Badge,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  Alert,
  Form,
  FormGroup,
} from "reactstrap";
import { isEmpty } from "lodash";
import { useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import Icon from "../../../components/Icon/Icon";
import PaginationComponent from "./../../../components/Pagination/Pagination";
import TableFilter from "../../../components/TableFilter/TableFilter";
import PageHeader from "../../../components/PageHeader/PageHeader";
import SearchTable from "../../../components/SearchTable/SearchTable";
import InfoItem from "./../../../helpers/InfoItem/InfoItem";
import UserAvatar from "./../../../components/UserAvatar/UserAvatar";
import UserInfoItem from "./../../../components/UserInfoItem/UserInfoItem";
import ErrorMessage from "./../../../helpers/ErrorMessage/ErrorMessage";
import Button from "../../../components/Button/Button";
import Currency, { ConvertCurrency } from "../../../helpers/Currency/Currency";
import { Block } from "./../../../components/Block/Block";
import {
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
} from "./../../../components/DataTable/DataTable";
import { Col, Row } from "./../../../components/Grid/Grid";
import { receptionOrderUserContext } from "../../../context/UserContext/ReceptionOrderUserContext/ReceptionOrderUserContext";
import { convertDate, numberSeparate } from "../../../utils";
import {
  categoryItems,
  convertReceptionOrderStatusToColor,
  convertReceptionOrderStatusToText,
  receptionOrderStatusItems,
} from "../../../utils/enum";
import { changeReceptionOrderStatusValidation } from "../../../utils/validation/validation";

const ReceptionOrdersUser = () => {
  const auth = useSelector((state) => state.auth);
  const loader = useSelector((state) => state.loader);

  const {
    receptionOrders,
    receptionOrder,
    setReceptionOrder,
    filter,
    setFilter,
    sort,
    setSort,
    start,
    setStart,
    end,
    setEnd,
    status,
    setStatus,
    pageCount,
    itemsCount,
    setItemsCount,
    pageNumber,
    setPageNumber,
    handleGetReceptionOrders,
    handleChangeReceptionOrderStatus,
  } = useContext(receptionOrderUserContext);

  useEffect(() => {
    handleGetReceptionOrders();
  }, [start, end, status, pageNumber, itemsCount, sort]);

  // Validation
  const {
    register,
    watch,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(changeReceptionOrderStatusValidation),
  });

  return (
    <>
      <SiteTitle title="سفارشات پذیرنده" />

      <PageHeader title="سفارشات پذیرنده" />

      <TableFilter
        setItemsCount={setItemsCount}
        itemsCount={itemsCount}
        setSort={setSort}
        sort={sort}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
        setReceptionOrderStatus={setStatus}
        receptionOrderStatus={status}
      />

      <Block>
        <DataTable className="card-stretch">
          <SearchTable
            filterPlaceholder="کد پیگیری و ..."
            filter={filter}
            setFilter={setFilter}
            onSearch={handleGetReceptionOrders}
          />
          <DataTableBody>
            <DataTableHead>
              <DataTableRow>
                <span className="sub-text">#</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">کاربر</span>
              </DataTableRow>
              <DataTableRow size="md">
                <span className="sub-text">پذیرنده</span>
              </DataTableRow>
              <DataTableRow size="sm">
                <span className="sub-text">جمع کل</span>
              </DataTableRow>
              <DataTableRow size="lg">
                <span className="sub-text">کد پیگیری</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">وضعیت</span>
              </DataTableRow>
              <DataTableRow size="xl">
                <span className="sub-text">تاریخ ایجاد</span>
              </DataTableRow>
              <DataTableRow />
            </DataTableHead>
            {/*Head*/}
            {receptionOrders.length > 0
              ? receptionOrders.map((item, index) => {
                  return (
                    <DataTableItem key={item._id}>
                      <DataTableRow>
                        <span>{index + 1}</span>
                      </DataTableRow>
                      <DataTableRow>
                        <div className="user-card">
                          <UserAvatar
                            {...(item?.customer?.user?.avatar
                              ? { image: item?.customer?.user?.avatar }
                              : {
                                  icon: "user-alt",
                                })}
                          />
                          <UserInfoItem
                            title={`${item?.customer?.user?.firstName} ${item?.customer?.user?.lastName}`}
                            subTitle={item?.customer?.user?.phoneNumber}
                          />
                        </div>
                      </DataTableRow>
                      <DataTableRow size="md">
                        <div className="user-card">
                          <UserAvatar
                            {...(item?.reception?.info?.avatar
                              ? { image: item?.reception?.info?.avatar }
                              : {
                                  icon: "user-alt",
                                })}
                          />
                          <UserInfoItem
                            title={item?.reception?.info?.title}
                            subTitle={item?.reception?.info?.address}
                          />
                        </div>
                      </DataTableRow>
                      <DataTableRow size="sm">
                        {item.totalAmount ? (
                          <>
                            <ConvertCurrency price={item.totalAmount} />{" "}
                            <Currency />
                          </>
                        ) : (
                          "اعتباری"
                        )}
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <span>{item.trackingCode}</span>
                      </DataTableRow>
                      <DataTableRow>
                        <Badge
                          className="badge-dim"
                          color={convertReceptionOrderStatusToColor(
                            item?.status
                          )}
                        >
                          {convertReceptionOrderStatusToText(item?.status)}
                        </Badge>
                      </DataTableRow>
                      <DataTableRow size="xl">
                        <span>
                          {convertDate(item.createdAt, "YYYY/MM/DD - HH:mm")}
                        </span>
                      </DataTableRow>

                      <DataTableRow className="nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1">
                          <li>
                            <UncontrolledDropdown>
                              <DropdownToggle
                                tag="a"
                                className="dropdown-toggle btn btn-icon btn-trigger"
                              >
                                <Icon name="more-h" />
                              </DropdownToggle>
                              <DropdownMenu
                                left
                                className="dropdown_max_height"
                              >
                                <ul className="link-list-opt no-bdr text-right">
                                  <li
                                    onClick={() => {
                                      setReceptionOrder(item);
                                    }}
                                  >
                                    <DropdownItem tag="a" type="button">
                                      <Icon name="eye" />
                                      <span>جزئیات</span>
                                    </DropdownItem>
                                  </li>
                                  {((auth.isReception &&
                                    (item.status ===
                                      receptionOrderStatusItems[10] ||
                                      item.status ===
                                        receptionOrderStatusItems[8])) ||
                                    (auth.isAgent &&
                                      item.status ===
                                        receptionOrderStatusItems[7])) && (
                                    <li
                                      onClick={() => {
                                        setReceptionOrder({
                                          ...item,
                                          isStatus: true,
                                        });
                                        reset();
                                      }}
                                    >
                                      <DropdownItem tag="a" type="button">
                                        <Icon name="edit" />
                                        <span>تغییر وضعیت</span>
                                      </DropdownItem>
                                    </li>
                                  )}
                                </ul>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </li>
                        </ul>
                      </DataTableRow>
                    </DataTableItem>
                  );
                })
              : null}
          </DataTableBody>
          <div className="card-inner">
            {receptionOrders.length > 0 ? (
              <PaginationComponent
                pageCount={pageCount}
                currentPage={pageNumber}
                setPageNumber={setPageNumber}
              />
            ) : (
              <div className="text-center">
                <span className="text-silent">داده ای برای نمایش یافت نشد</span>
              </div>
            )}
          </div>
        </DataTable>
      </Block>

      <Modal
        isOpen={!isEmpty(receptionOrder) && !receptionOrder.isStatus}
        toggle={() => setReceptionOrder({})}
        className="modal-dialog-centered"
        size="md"
      >
        <ModalBody>
          <span
            type="button"
            onClick={() => setReceptionOrder({})}
            className="close"
          >
            <Icon name="cross-sm" />
          </span>
          <div className="p-2">
            <h5 className="title">جزئیات سفارش {receptionOrder.fullName}</h5>
            <br />
            <span className="text-primary border-bottom fw-bold">
              اطلاعات پذیرنده
            </span>
            <Row>
              <Col sm="6">
                <InfoItem title="نام">
                  {receptionOrder.reception?.info?.title}
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="آدرس">
                  {receptionOrder.reception?.info?.address}
                </InfoItem>
              </Col>
            </Row>
            <br />
            <span className="text-primary border-bottom fw-bold">
              اطلاعات درخواست دهنده
            </span>
            <Row>
              <Col sm="6">
                <InfoItem title="نام">
                  {receptionOrder.customer?.user?.firstName}
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="نام خانوادگی">
                  {receptionOrder.customer?.user?.lastName}
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="شماره موبایل">
                  {receptionOrder.customer?.user?.phoneNumber}
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="کدملی">
                  {receptionOrder.customer?.user?.nationalNumber}
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="کد">
                  {receptionOrder.customer?.user?.code}
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="تاریخ تولد">
                  {convertDate(receptionOrder.customer?.user?.birthDate)}
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="سمت">
                  {receptionOrder.customer?.user?.post}
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="تعداد افراد تحت پوشش">
                  {receptionOrder.customer?.user?.numberOfCovered}
                </InfoItem>
              </Col>
            </Row>
            <br />
            <span className="text-primary border-bottom fw-bold">
              اطلاعات سفارش
            </span>
            <Row>
              {receptionOrder?.reception?.info?.type === categoryItems[0] && (
                <>
                  <Col sm="6">
                    <InfoItem title="تاریخ ورود">
                      {convertDate(receptionOrder?.travel?.from)}
                    </InfoItem>
                  </Col>
                  <Col sm="6">
                    <InfoItem title="تاریخ خروج">
                      {convertDate(receptionOrder?.travel?.to)}
                    </InfoItem>
                  </Col>
                </>
              )}
              <Col sm="6">
                <InfoItem title="کد پیگیری">
                  {receptionOrder.trackingCode}
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="تاریخ ثبت سفارش">
                  {convertDate(receptionOrder.createdAt, "YYYY/MM/DD - HH:mm")}
                </InfoItem>
              </Col>
              {!isEmpty(receptionOrder.customer?.user) && (
                <Col sm="6">
                  <InfoItem title="مبلغ کل">
                    {receptionOrder.totalAmount ? (
                      <>
                        <ConvertCurrency price={receptionOrder.totalAmount} />{" "}
                        <Currency />
                      </>
                    ) : (
                      "اعتباری"
                    )}
                  </InfoItem>
                </Col>
              )}
              <Col sm="6">
                <InfoItem title="وضعیت سفارش">
                  <Badge
                    className="badge-dim"
                    color={convertReceptionOrderStatusToColor(
                      receptionOrder?.status
                    )}
                  >
                    {convertReceptionOrderStatusToText(receptionOrder?.status)}
                  </Badge>
                </InfoItem>
              </Col>
              {receptionOrder.reason && (
                <Col size="12" className="mt-2">
                  <Alert color="warning" className="text-justify">
                    <InfoItem title="دلیل">{receptionOrder.reason}</InfoItem>
                  </Alert>
                </Col>
              )}
            </Row>
            <br />
            <Alert color="info" className="text-justify">
              <InfoItem title="توضیحات">{receptionOrder.description}</InfoItem>
            </Alert>

            <span className="text-primary border-bottom fw-bold">
              اطلاعات {receptionOrder?.products?.length} محصول
            </span>
            {receptionOrder?.products?.map((item) => (
              <Row
                key={item?._id}
                className="border rounded mb-2 py-1 border-primary position-relative m-0"
              >
                <Col size="12" className="mb-2">
                  <h5 className="mt-1">{item?.title}</h5>
                </Col>
                <Col sm="6">
                  <InfoItem title="تعداد">
                    <Badge color="primary">{numberSeparate(item?.count)}</Badge>
                  </InfoItem>
                </Col>
                {!!item.price && (
                  <Col sm="6">
                    <InfoItem title="قیمت">
                      <ConvertCurrency price={item.price} /> <Currency />
                    </InfoItem>
                  </Col>
                )}
                {!!item.price && (
                  <Col sm="6">
                    <InfoItem title="قیمت کل">
                      <ConvertCurrency price={item.price * item?.count} />{" "}
                      <Currency />
                    </InfoItem>
                  </Col>
                )}
                <Col sm="12">
                  <InfoItem title="توضیحات">{item?.description}</InfoItem>
                </Col>
              </Row>
            ))}

            {receptionOrder?.reception?.info?.type === categoryItems[0] && (
              <>
                <span className="text-primary border-bottom fw-bold">
                  اطلاعات {receptionOrder?.travel?.guests?.length} مسافر
                </span>
                {receptionOrder?.travel?.guests?.map((item) => (
                  <Row
                    key={item?._id}
                    className="border rounded mb-2 py-1 border-primary position-relative m-0"
                  >
                    <Col sm="6">
                      <InfoItem title="نام">{item.first_name}</InfoItem>
                    </Col>
                    <Col sm="6">
                      <InfoItem title="نام خانوادگی">{item.last_name}</InfoItem>
                    </Col>
                  </Row>
                ))}
              </>
            )}
          </div>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={receptionOrder.isStatus}
        toggle={() => setReceptionOrder({})}
        className="modal-dialog-centered"
        size="md"
      >
        <ModalBody>
          <span
            type="button"
            onClick={() => setReceptionOrder({})}
            className="close"
          >
            <Icon name="cross-sm" />
          </span>{" "}
          <div className="p-2">
            <h5 className="title">ویرایش سفارش پذیرنده</h5>
            <br />
            <Form
              className="is-alter"
              onSubmit={handleSubmit(handleChangeReceptionOrderStatus)}
            >
              <Row className="gy-4">
                <Col size="12">
                  <FormGroup>
                    <label className="form-label" htmlFor="status">
                      وضعیت
                    </label>
                    <div className="form-control-wrap">
                      <select
                        className="form-control-lg form-control"
                        {...register("status")}
                      >
                        <option value="" selected hidden>
                          وضعیت را انتخاب نمایید
                        </option>
                        <option
                          value={
                            receptionOrderStatusItems[auth.isAgent ? 8 : 11]
                          }
                          selected={
                            receptionOrderStatusItems[auth.isAgent ? 8 : 11] ===
                            watch("status")
                          }
                        >
                          تایید سفارش
                        </option>
                        <option
                          value={
                            receptionOrderStatusItems[auth.isAgent ? 9 : 12]
                          }
                          selected={
                            receptionOrderStatusItems[auth.isAgent ? 9 : 12] ===
                            watch("status")
                          }
                        >
                          رد سفارش
                        </option>
                      </select>
                      <ErrorMessage message={errors.status?.message} />
                    </div>
                  </FormGroup>
                </Col>

                <Col size="12">
                  <FormGroup>
                    <label className="form-label" htmlFor="reason">
                      دلیل
                    </label>
                    <div className="form-control-wrap">
                      <textarea
                        type="text"
                        placeholder="دلیل را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("reason")}
                      />
                      <ErrorMessage message={errors.reason?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col size="12">
                  <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                    <li>
                      <Button
                        color="primary"
                        size="lg"
                        type="submit"
                        loading={loader}
                      >
                        ویرایش وضعیت
                      </Button>
                    </li>
                    <li>
                      <button
                        type="button"
                        onClick={() => setReceptionOrder({})}
                        className="link link-light"
                      >
                        انصراف
                      </button>
                    </li>
                  </ul>
                </Col>
              </Row>
            </Form>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default ReceptionOrdersUser;
